import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  MessageCircleQuestionIcon,
  XIcon,
} from 'lucide-react';
import type { DocumentRegistryEntry } from '@kennisbank/shared';
import { ApiError, apiGet, apiGetBlob } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { openExternalUrl, safeHttpsHref } from '../lib/url.js';
import { WijzigingsverzoekLink } from './WijzigingsverzoekButton.js';

/**
 * Documentvoorbeeld in een venster (Outlook-stijl, prototype-keuze Jos 8 okt
 * 2026, branch prototype/documentpreview). PDF en afbeeldingen toont de
 * browser zelf via de bestaande signed URL; .docx tekent docx-preview in de
 * browser (bytes via de backend, de bucket heeft geen CORS). Een oude .doc
 * komt als .docx-kopie die de ingest maakt; .odt valt terug op downloaden.
 */
type Kind = 'pdf' | 'image' | 'docx' | 'none';

function kindOf(filename: string): Kind {
  if (/\.pdf$/i.test(filename)) return 'pdf';
  if (/\.(jpe?g|jfif|png|webp|gif)$/i.test(filename)) return 'image';
  if (/\.docx?$/i.test(filename)) return 'docx';
  return 'none';
}

/**
 * Alleen `filename` is verplicht: de declaratiebonnen gebruiken dezelfde
 * viewer, met een eigen `getUrl` en eigen gegevenspaneel (`meta`).
 */
export type PreviewDoc = Pick<DocumentRegistryEntry, 'filename'> & Partial<DocumentRegistryEntry>;

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

export function DocumentPreview<D extends PreviewDoc>({
  doc,
  docs,
  onSelect,
  onClose,
  getUrl,
  meta: metaOverride,
}: {
  doc: D;
  /** De zichtbare lijst, voor vorige/volgende. */
  docs: D[];
  onSelect: (d: D) => void;
  onClose: () => void;
  /** Andere bron dan de kennisbank (declaratiebonnen). */
  getUrl?: (filename: string) => Promise<string>;
  meta?: ReactNode;
}): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Gekoppeld aan de bestandsnaam: anders wijst de eerste render na wisselen
  // nog naar de signed URL van het vorige document.
  const [signed, setSigned] = useState<{ filename: string; url: string } | null>(null);
  const [error, setError] = useState<{ filename: string; message: string } | null>(null);
  const url = signed?.filename === doc.filename ? safeHttpsHref(signed.url) || null : null;
  const errorMessage = error?.filename === doc.filename ? error.message : null;
  // Word tekenen kan alleen via de content-route van de kennisbank; een
  // Word-bon valt daarom terug op downloaden.
  const kind = getUrl && kindOf(doc.filename) === 'docx' ? 'none' : kindOf(doc.filename);
  const title = doc.documentTitle || doc.filename;
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // `voorbeeld` laat de auditregel "bekeken" zeggen in plaats van
  // "gedownload"; het bulk-alarm telt alleen echte downloads.
  const fetchUrl = (filename: string, voorbeeld = false): Promise<string | null> =>
    (getUrl
      ? getUrl(filename)
      : apiGet<{ url: string }>(
          `/api/v1/documents/${encodeURIComponent(filename)}/download-url${voorbeeld ? '?voor=voorbeeld' : ''}`,
        ).then((res) => res.url)
    )
      .then((u) => {
        setSigned({ filename, url: u });
        return u;
      })
      .catch((err: unknown) => {
        setError({
          filename,
          message:
            err instanceof ApiError && err.code === 'source_not_in_bucket'
              ? 'Dit bronbestand staat niet meer in de opslag. Dit komt voor bij oudere documenten.'
              : 'Kon het document niet laden.',
        });
        return null;
      });

  // Word haalt zijn bytes zelf op; de signed URL (en dus de auditregel
  // "gedownload") komt daar pas bij een klik op Downloaden.
  useEffect(() => {
    if (kind !== 'docx') void fetchUrl(doc.filename, true);
  }, [doc.filename, kind]);

  // Focus in het venster bij openen, terug naar de titel bij sluiten.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => prev?.focus();
  }, []);

  const i = docs.findIndex((d) => d.filename === doc.filename);
  const step = (delta: number): void => {
    if (docs.length > 1) onSelect(docs[(i + delta + docs.length) % docs.length]!);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Esc niet laten sluiten tijdens typen of met het Wijzigingsverzoek
      // erbovenop: dat gooide het halfgetypte verzoek weg. Bladeren gaat via
      // de knoppen; pijltjes scrollen gewoon.
      // Alleen binnen dit venster tellen: de meldingenbel heeft ook role=dialog.
      if (e.key !== 'Escape') return;
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (dialogRef.current?.querySelector('[role="dialog"]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    // Achtergrond niet laten meescrollen onder het venster.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  });

  const download = async (): Promise<void> => {
    // De URL van het voorbeeld staat als "bekeken" in de audit log. Een
    // losse opvraging legt de klik vast als "gedownload". Niet via fetchUrl:
    // die zet een nieuwe URL en dan laadt het voorbeeld opnieuw. Openen gebeurt
    // met de URL die er al is, want na een await blokkeert Safari het venster.
    if (url && !getUrl) {
      void apiGet(`/api/v1/documents/${encodeURIComponent(doc.filename)}/download-url`).catch(
        () => undefined,
      );
    }
    const target = url ?? (kind === 'docx' ? await fetchUrl(doc.filename) : null);
    if (target && !openExternalUrl(target)) {
      setError({ filename: doc.filename, message: 'Ongeldige download-URL.' });
    }
  };
  // Touchtoestellen tonen een PDF in een iframe niet goed (Android: leeg,
  // iOS: alleen pagina 1); daar openen we hem in de eigen PDF-viewer.
  const touch = window.matchMedia('(pointer: coarse)').matches;

  const meta = metaOverride ?? (
    <>
      <dl className="space-y-4">
        <MetaRow label="Auteur">{doc.author || '—'}</MetaRow>
        <MetaRow label="Type">{doc.documentType || '—'}</MetaRow>
        <MetaRow label="Geüpdatet">{formatDate(doc.lastIngestedAt)}</MetaRow>
        <MetaRow label="Specialisaties">
          {doc.specialisations?.length ? (
            <div className="flex flex-wrap gap-1">
              {doc.specialisations.map((s) => (
                <span
                  key={s}
                  className="rounded-full bg-mosterdgeel-50 px-2 py-0.5 text-[11px] font-medium text-mosterdgeel-600"
                >
                  {s}
                </span>
              ))}
            </div>
          ) : (
            '—'
          )}
        </MetaRow>
        <MetaRow label="Bestand">
          <span className="break-all">{doc.filename}</span>
          {doc.sizeBytes ? ` · ${Math.max(1, Math.round(doc.sizeBytes / 1000))} kB` : ''}
        </MetaRow>
      </dl>
      <div className="mt-6 space-y-2">
        {(user?.aiTools || user?.demo) && !doc.documentOnly && (
          <button
            type="button"
            onClick={() => navigate('/kennisagent', { state: { draft: `Over "${title}": ` } })}
            className="flex w-full items-center gap-2 rounded-lg border border-brand-200 px-3 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
          >
            <MessageCircleQuestionIcon size={16} aria-hidden="true" /> Vraag Mila over dit document
          </button>
        )}
        <div className="px-3 py-1 text-sm">
          <WijzigingsverzoekLink
            context={{
              surface: 'documenten',
              filename: doc.filename,
              documentTitle: title,
              author: doc.author ?? '',
              specialisations: doc.specialisations ?? [],
            }}
          />
        </div>
      </div>
    </>
  );

  let body: ReactNode;
  // Word laadt zijn eigen bytes en meldt zijn eigen fouten; de signed URL is
  // daar alleen voor de downloadknop.
  if (kind === 'docx') {
    body = <DocxView key={doc.filename} filename={doc.filename} />;
  } else if (errorMessage) {
    body = <Notice>{errorMessage}</Notice>;
  } else if (!url) {
    body = <Notice>Document laden…</Notice>;
  } else if (kind === 'pdf' && touch) {
    body = (
      <Notice>
        Op dit apparaat opent de PDF in de eigen PDF-weergave.
        <button
          type="button"
          onClick={() => void download()}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          PDF openen
        </button>
      </Notice>
    );
  } else if (kind === 'pdf') {
    body = <iframe title={`Voorbeeld van ${title}`} src={url} className="h-full w-full" />;
  } else if (kind === 'image') {
    body = (
      <div className="flex h-full items-center justify-center overflow-auto p-4">
        <img src={url} alt={title} className="max-h-full max-w-full object-contain" />
      </div>
    );
  } else {
    body = (
      <Notice>
        Van dit bestandstype is nog geen voorbeeld beschikbaar.
        <button
          type="button"
          onClick={() => void download()}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          <DownloadIcon size={16} aria-hidden="true" /> Downloaden
        </button>
      </Notice>
    );
  }

  const iconBtn = 'rounded-lg p-2 text-ink-900/70 hover:bg-brand-50 hover:text-ink-900';

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 sm:p-6" onClick={onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex h-full flex-col overflow-hidden bg-white shadow-2xl sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-1 border-b border-brand-100 px-3 py-2 sm:gap-2 sm:px-4">
          <h2 className="min-w-0 flex-1 truncate font-semibold text-ink-900">{title}</h2>
          <button
            type="button"
            onClick={() => void download()}
            disabled={!url && kind !== 'docx'}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <DownloadIcon size={16} aria-hidden="true" />
            <span className="hidden sm:inline">Downloaden</span>
          </button>
          {docs.length > 1 && (
            <>
              <span className="mx-1 hidden h-6 w-px bg-brand-100 sm:block" />
              <button
                type="button"
                onClick={() => step(-1)}
                title="Vorige"
                aria-label="Vorige document"
                className={`${iconBtn} hidden sm:block`}
              >
                <ChevronLeftIcon size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                title="Volgende"
                aria-label="Volgende document"
                className={`${iconBtn} hidden sm:block`}
              >
                <ChevronRightIcon size={18} aria-hidden="true" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            ref={closeRef}
            title="Sluiten (Esc)"
            aria-label="Sluiten"
            className={iconBtn}
          >
            <XIcon size={18} aria-hidden="true" />
          </button>
        </header>
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div className="min-h-0 flex-1 bg-slate-100">{body}</div>
          {/* Op mobiel gaat het document voor; de gegevens klappen weg. */}
          <details className="border-t border-brand-100 lg:hidden">
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-ink-800">
              Gegevens en acties
            </summary>
            <div className="max-h-[50vh] overflow-y-auto px-4 pb-4">{meta}</div>
          </details>
          <aside className="hidden w-80 shrink-0 overflow-y-auto border-l border-brand-100 p-5 lg:block">
            {meta}
          </aside>
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-ink-900/50">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink-900">{children}</dd>
    </div>
  );
}

function Notice({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center p-6 text-center text-sm text-ink-900/60">
      {children}
    </div>
  );
}

function DocxView({ filename }: { filename: string }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'tooLarge'>('loading');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [{ blob }, { renderAsync }] = await Promise.all([
          apiGetBlob(`/api/v1/documents/${encodeURIComponent(filename)}/content`),
          // Los geladen: docx-preview + jszip horen niet in de hoofdbundel.
          import('docx-preview'),
        ]);
        if (cancelled || !ref.current) return;
        // Shadow root: de stijlen uit het Word-bestand gaan ongefilterd een
        // <style> in en mogen de rest van de app niet kunnen overschrijven.
        const root = ref.current.shadowRoot ?? ref.current.attachShadow({ mode: 'open' });
        // Op een smal scherm laat een A4-pagina (~800px) alleen piepkleine
        // tekst over; daar loopt de tekst door op schermbreedte, zonder pagina's.
        const narrow = (ref.current.parentElement?.clientWidth ?? 0) < 640;
        await renderAsync(blob, root as unknown as HTMLElement, root as unknown as HTMLElement, {
          inWrapper: !narrow,
          ignoreWidth: narrow,
          ignoreHeight: narrow,
          ignoreLastRenderedPageBreak: true,
          // data:-URL's i.p.v. blob:, want de CSP (img-src) staat geen blob: toe;
          // anders verdwijnen alle afbeeldingen uit Word-documenten in productie.
          useBase64URL: true,
          // Ingesloten HTML (altChunk) zou als same-origin iframe renderen.
          renderAltChunks: false,
          renderComments: false,
        });
        if (narrow) {
          // Paginamarges van 2,5 cm vreten de halve schermbreedte op.
          root
            .querySelectorAll<HTMLElement>('section.docx')
            .forEach((sec) => (sec.style.padding = '16px'));
        }
        // Links in een document mogen alleen naar het web of mail wijzen.
        root.querySelectorAll('a[href]').forEach((a) => {
          if (!/^(https?:|mailto:|#)/i.test(a.getAttribute('href') ?? ''))
            a.removeAttribute('href');
          else a.setAttribute('target', '_blank');
        });
        if (!cancelled) setState('ready');
      } catch (err) {
        if (!cancelled)
          setState(err instanceof ApiError && err.status === 413 ? 'tooLarge' : 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filename]);

  return (
    <div className={`h-full overflow-auto ${state === 'ready' ? 'bg-white sm:bg-[gray]' : ''}`}>
      {state === 'loading' && <Notice>Document laden…</Notice>}
      {state === 'tooLarge' && (
        <Notice>Dit Word-bestand is te groot voor een voorbeeld. Download het bestand.</Notice>
      )}
      {state === 'error' && (
        <Notice>Dit Word-bestand kon niet worden weergegeven. Download het bestand.</Notice>
      )}
      {/* docx-preview zet zijn eigen grijze achtergrond (gray) en pagina's neer. */}
      <div ref={ref} className={state === 'ready' ? '' : 'hidden'} />
    </div>
  );
}
